<template>
  <h2 class="mx-0h">{{ $t('unit.timeline.header') }}</h2>
  <div class="button-grid mb-3">
    <BtnComponent
      v-for="(category, i) in timelineCategories"
      :key="i"
      :outline="filter !== category.category"
      :color="category.color"
      @click="changeFilter(category.category)"
    >
      {{ category.name }}
    </BtnComponent>
  </div>

  <EventCard
    v-for="(timelineEntry, i) in timeline.entries"
    :key="i"
    :timeline-entry="timelineEntry"
    :categories="timelineCategories"
    :sn="sn"
  />

  <div
    v-if="$apollo.loading"
    class="text-center"
  >
    <BtnComponent loading disabled>&nbsp;</BtnComponent>
  </div>

  <div v-if="!$apollo.loading" class="text-center pt-5 pb-5">
    <BtnComponent
      v-if="!allLoaded"
      outline
      @click="loadMore"
    >
      {{ $t('unit.timeline.loadMore') }}
    </BtnComponent>
    <div v-else>
      {{ $t('unit.timeline.allLoaded') }}
    </div>
  </div>
</template>

<script>
import EventCard from '@acs/components-library/src/components/deviceDetail/timeline/EventCard.vue';
import BtnComponent from '@acs/components-library/src/components/common/Btn.vue';

import { ViewQuery } from '@acs/components-library/src/gql/live/UnitDetailTimeline.js';
import { isDemo } from '@acs/components-library/src/demo.js';

export default {
  components: {
    BtnComponent,
    EventCard,
  },
  props: {
    sn: {
      type: String,
      required: true,
    },
    unpaired: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  data() {
    return {
      filter: null,
      allLoaded: false,
      timelineCategories: [
        {
          category: 'CALIBRATION',
          color: '#00C0FF',
          name: this.$t('unit.timeline.calibration'),
        },
        {
          category: 'CONFIGURATION',
          color: '#A5A5A5',
          name: this.$t('unit.timeline.configuration'),
        },
        {
          category: 'ERROR',
          color: '#FF0000',
          name: this.$t('unit.timeline.error'),
        },
        {
          category: 'REQUIRED',
          color: '#5AB469',
          name: this.$t('unit.timeline.required'),
        },
      ],
    };
  },
  apollo: {
    unitBySerialNumber: {
      query: ViewQuery,
      variables() {
        const vars = {
          sn: this.sn,
          timelineFirst: 10,
          timelineAfter: null,
        };
        if (this.filter && !isDemo()) {
          vars.timelineCategory = this.filter;
        }
        return vars;
      },
      skip() {
        return this.unpaired || !this.sn;
      },
      result({ data }) {
        if (data.unitBySerialNumber.timeline.entries.length < 10) {
          this.allLoaded = true;
        }
      },
    },
  },
  computed: {
    unit() {
      return this.unitBySerialNumber;
    },
    timeline() {
      if (isDemo() && this.filter) {
        return {
          entries: this.unit?.timeline.entries.filter(
            (entry) => entry.category === this.filter,
          ),
          cursor: null,
        };
      }
      return this.unit?.timeline || { entries: [], cursor: null };
    },
  },
  methods: {
    changeFilter(type) {
      this.allLoaded = false;
      if (!this.filter) {
        this.filter = type;
      } else if (this.filter) {
        if (this.filter !== type) this.filter = type;
        else if (this.filter === type) this.filter = null;
      }
    },
    async loadMore() {
      if (this.lastFetchMoreAt && Date.now() - this.lastFetchMoreAt < 1000) {
        console.warn('implement snackbar');
        return;
      } else {
        this.lastFetchMoreAt = Date.now();
      }

      const variables = {
        sn: this.sn,
        timelineFirst: 10,
        timelineAfter: this.unit.timeline.cursor,
      };

      await this.$apollo.queries.unitBySerialNumber.fetchMore({
        variables,
        updateQuery: (previousResult, { fetchMoreResult }) => {
          const { entries: prevEntries } = previousResult.unitBySerialNumber.timeline;
          const {
            entries: newEntries,
            cursor: newCursor,
          } = fetchMoreResult.unitBySerialNumber.timeline;

          if (newEntries.length === 0) {
            return previousResult;
          }

          this.allLoaded = newEntries.length < 10;

          return {
            unitBySerialNumber: {
              ...previousResult.unitBySerialNumber,
              timeline: {
                cursor: newCursor,
                entries: [
                  ...prevEntries,
                  ...newEntries,
                ],
                __typename: 'Timeline',
              },
            },
          };
        },
      });

    },
  },
};
</script>

<style lang="scss" scoped>
.button-grid {
  display: flex;
  justify-items: stretch;
  gap: .25rem;

  .btn {
    margin: 0;
    font-size: 0.8125rem;
    //flex: 1 1;
    flex: auto;
    padding: .75rem .25rem;
  }
}

@media (min-width: 22.5rem) {
  .button-grid {
    .btn {
      flex: 1 1;
    }
  }
}
</style>
